import React, {ReactNode, useCallback, useEffect, useState} from 'react';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {BasicObject, NavigatorScreenProps, SafeAny} from '@/types';
import globalStore from '@/services/global.state';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import {toGame} from '../game-navigate';
import {ActivityIndicator, SectionList, View} from 'react-native';
import {
  GameListItem,
  LiveBannerItem,
  LiveHotGameItem,
  getBannersList,
  getGameList,
  getLiveHotGameList,
} from './live-casino-service';
import Text from '@/components/basic/text';
import LiveVideo from './live-casino-video';
import LiveCarousel from './live-casino-carousel';
import LiveListItem from './live-casino-list-item';
import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import {useFocusEffect} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import LiveCasinoTabs from '@/common-pages/live-casino/live-casino-tabs';
import {getGameCategory} from '@/pages/casino/casino-service';
import CasinoSelect from '@/pages/casino/casino-select';
import {googleAnalysis} from '@utils/google-analysis';

const pageSize = 21;

function nElements(arr: SafeAny[], n: number) {
  const result = [];
  let i = 0;
  while (arr.length > i * n) {
    result.push(arr.slice(i * n, ++i * n));
  }
  return result;
}

const LiveCasino = (
  props: NavigatorScreenProps & {renderAmount?: ReactNode},
) => {
  const {calcActualSize} = useScreenSize();
  const {route, renderAmount} = props;
  // categoryId在Singam和Dailylotto都是默认6为Live Casino，故这里如果未传入则先使用默认值
  const categoryId = (route.params as BasicObject)?.categoryId || '6';
  const [pageLoading, setPageLoading] = React.useState(false);
  const [bannerList, setBannerList] = useState<LiveBannerItem[]>([]);
  const [hotGameList, setHotGameList] = useState<LiveHotGameItem[]>([]);
  const [gameList, setGameList] = useState<GameListItem[][]>([]);
  const [loadMore, setLoadMore] = useState<boolean>(false);
  const pageRef = React.useRef(1);
  const onEndReachedCalledDuringMomentum = React.useRef(true);
  const [hasMore, setHasMore] = React.useState(true);
  const firstLock = React.useRef(true);
  const [scrollY, setScrollY] = React.useState(0);
  const [headerHeight, setHeaderHeight] = React.useState(200);
  const [gameTypes, setGameTypes] = useState<{label: string; value: string}[]>(
    [],
  );
  const [gameType, setGameType] = useState('');
  const [provider, setProvider] = useState<string>('');
  const [sort, setSort] = useState<string>('');

  const initAmount = useCallback(() => {
    globalStore.token && globalStore.updateAmount.next({});
  }, []);
  useFocusEffect(initAmount);

  const loadGameList = async (refresh: boolean) => {
    try {
      if (pageLoading || loadMore) {
        return;
      }
      if (refresh) {
        pageRef.current = 1;
        setPageLoading(true);
      } else {
        pageRef.current += 1;
        setLoadMore(true);
      }
      const params: SafeAny = {
        categoryId,
        l2CategoryId: gameType,
        // provider,
        providerId: provider,
        pageNo: pageRef.current,
        pageSize,
      };
      if (sort) {
        params.orderByField = 'name';
        params.isAsc = sort === 'A-Z';
      }
      const res = await getGameList(params);
      if (res.content) {
        const newArr = nElements(res.content, 3);
        setGameList(refresh ? newArr : gameList.concat(newArr));
        setHasMore(res.content.length >= pageSize);
      }
    } finally {
      setPageLoading(false);
      setLoadMore(false);
    }
  };

  const onLoadMore = async () => {
    if (hasMore && !onEndReachedCalledDuringMomentum.current) {
      await loadGameList(false);
      onEndReachedCalledDuringMomentum.current = true;
    }
  };

  const initGames = async () => {
    setPageLoading(true);
    try {
      const [_, banner, hot] = await Promise.allSettled([
        loadGameList(true),
        getBannersList(),
        getLiveHotGameList(),
      ]);
      if (banner.status === 'fulfilled' && banner.value?.length > 0) {
        setBannerList(banner.value);
      } else {
        setBannerList([]);
      }
      setHotGameList(hot.status === 'fulfilled' ? hot.value : []);
    } finally {
      setPageLoading(false);
      firstLock.current = false;
    }
  };

  const Footer = React.useMemo(() => {
    if (loadMore) {
      return (
        <View style={[theme.flex.center, theme.padding.l]}>
          <ActivityIndicator />
        </View>
      );
    }
    if (gameList.length > 0 && !hasMore) {
      return (
        <View style={[theme.padding.btml]}>
          <Text textAlign="center" size="medium" color={'#9FA5AC'}>
            No more data
          </Text>
        </View>
      );
    }
    return null;
  }, [loadMore, gameList, hasMore]);

  // 兼容处理 videoUri
  const getMp4 = (item: LiveBannerItem) =>
    item.bannerImg.endsWith('mp4') ? item.bannerImg : item.otherParams;
  const videoUri = bannerList.length > 0 ? getMp4(bannerList[0]) : '';
  const {t} = useTranslation();
  useEffect(() => {
    initGames();
    getGameCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (firstLock.current) {
      return;
    }
    loadGameList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, sort, gameType]);
  const getGameCategories = () => {
    // 获取游戏分类
    getGameCategory('liveCasino').then(res => {
      const gamesList = res.map(v => {
        return {
          label: v.name,
          value: v.id,
        };
      });
      setGameTypes([{label: 'All', value: ''}, ...gamesList]);
    });
  };
  const handleGameTypeChange = (val: string) => {
    setGameType(val);
  };
  const handleChangeProvider = (value: string) => {
    setProvider(value);
  };

  const handleChangeSort = (value: string) => {
    setSort(value);
  };
  return (
    <View style={[theme.flex.flex1, theme.background.lightGrey]}>
      <DetailNavTitle
        leftNode={
          <span className="text-lg text-t1 font-bold">
            {t('home.tab.live')}
          </span>
        }
        title={' '}
        hideAmount={!!renderAmount}
        rightNode={renderAmount}
        hideServer
      />
      <Spin loading={pageLoading} style={[theme.flex.flex1]}>
        <View style={[theme.flex.flex1, theme.flex.basis0]}>
          <SectionList
            onScroll={e => {
              if (
                globalStore.isWeb &&
                onEndReachedCalledDuringMomentum.current
              ) {
                onEndReachedCalledDuringMomentum.current = false;
              }
              setScrollY(e.nativeEvent.contentOffset.y);
            }}
            // removeClippedSubviews={false}
            sections={[
              {
                title: '',
                data: gameList,
              },
            ]}
            // columnWrapperStyle={[{columnGap: 8}, theme.padding.lrl]}
            ListHeaderComponent={
              <View
                onLayout={e => setHeaderHeight(e.nativeEvent.layout.height)}>
                <LiveVideo
                  onPress={i => {
                    const current = bannerList[i];
                    googleAnalysis.liveCasinoBannerClick(current);
                    toGame({
                      source: current.source,
                      id: current.gameId,
                      gameUrl: current.gameLink,
                      tripartiteUniqueness: current.tripartiteUniqueness,
                    });
                  }}
                  videoUri={videoUri}
                />
                <LiveCarousel
                  onPress={(info: BasicObject) => {
                    googleAnalysis.liveCasinoBannerClick(info);
                    toGame({
                      source: info.source,
                      name: info.name || '',
                      id: info.gameId,
                      gameUrl: info.gameLink,
                      tripartiteUniqueness: info.tripartiteUniqueness,
                    });
                  }}
                  bannerList={hotGameList}
                />
              </View>
            }
            stickySectionHeadersEnabled
            renderSectionHeader={() => (
              <>
                <LiveCasinoTabs
                  tabOptions={gameTypes}
                  value={gameType}
                  onChange={handleGameTypeChange}
                />
                <CasinoSelect
                  type="liveCasino"
                  handleChangeProvider={handleChangeProvider}
                  handleChangeSort={handleChangeSort}></CasinoSelect>
              </>
            )}
            renderItem={({item, index}) => {
              return (
                <View
                  style={[
                    theme.flex.row,
                    {
                      paddingHorizontal: calcActualSize(theme.paddingSize.l),
                      columnGap: calcActualSize(theme.paddingSize.s),
                    },
                  ]}>
                  {item
                    .filter(pie => pie)
                    .map((pie, i) => (
                      <LiveListItem
                        isOld={false}
                        key={i}
                        pageLoading={pageLoading}
                        headerHeight={headerHeight}
                        scrollY={scrollY}
                        index={index}
                        onPress={() => {
                          toGame({
                            source: pie.source,
                            name: pie.name,
                            id: pie.id,
                            gameUrl: pie.gameUrl,
                            tripartiteUniqueness: pie.tripartiteUniqueness,
                          });
                        }}
                        gameInfo={pie}
                      />
                    ))}
                </View>
              );
            }}
            keyExtractor={(item, index) => `${index}`}
            onEndReachedThreshold={0.2}
            onMomentumScrollBegin={() => {
              onEndReachedCalledDuringMomentum.current = false;
            }}
            onEndReached={onLoadMore}
            ListFooterComponent={Footer}
          />
        </View>
      </Spin>
    </View>
  );
};

export default LiveCasino;
